import React from 'react';
import { Box, Container, Typography, Button, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FONT_FAMILY } from '../../theme';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import Hero from '../../components/Hero/Hero';
import FloatingBooking from '../../components/FloatingBooking/FloatingBooking';
import InfoSection from '../../components/InfoSection/InfoSection';
import GallerySection from '../../components/GallerySection/GallerySection';
import LanguageSwitcher from '../../components/LanguageSwitcher/LanguageSwitcher';

const galleryImages = [
  '/images/gallery-images/20231106_183210-scaled.jpg',
  '/images/gallery-images/20231021_200422-scaled.jpg',
  '/images/gallery-images/20240221_221259-scaled.jpg',
  '/images/gallery-images/20231014_1615451-scaled.jpg',
  '/images/gallery-images/20231202211434_IMG_0203-1-scaled.jpg',
  '/images/gallery-images/407919914_1225581092537491_4210383140292762734_n.jpg',
  '/images/gallery-images/396994153_1194706848958249_6821451955980784087_n-1.jpg',
  '/images/gallery-images/IMG_20231202_210827_141.webp',
  '/images/gallery-images/20231012_212624-scaled.jpg',
  '/images/gallery-images/20231101_210940-scaled.jpg',
  '/images/gallery-images/IMG_20231125_120650_287.webp',
  '/images/gallery-images/20231014_162438-scaled.jpg',
  '/images/gallery-images/20231210093606_IMG_0310-scaled.jpg',
  '/images/gallery-images/20231125113618_IMG_0076-scaled.jpg',
  '/images/gallery-images/1000006923-01-scaled.jpeg',
  '/images/gallery-images/20231204213504_IMG_0262-scaled.jpg',
  '/images/gallery-images/20231106_183229-scaled.jpg',
];

function getRandomImages(images, count = 4) {
  const arr = [...images];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr.slice(0, count);
}

export default function Home() {
  const { t, i18n } = useTranslation();
  const [language, setLanguage] = React.useState(() => {
    return i18n.language || localStorage.getItem('menuLanguage') || 'en';
  });

  // Sync language state with i18n.language when it changes (one-way: i18n -> state)
  React.useEffect(() => {
    if (i18n.language && i18n.language !== language) {
      setLanguage(i18n.language);
    }
  }, [i18n.language]);

  // When user changes language via toggle, update localStorage and i18n
  const handleLanguageChange = React.useCallback((newLang) => {
    setLanguage(newLang);
    localStorage.setItem('menuLanguage', newLang);
    i18n.changeLanguage(newLang);
  }, [i18n]);

  const randomGalleryImages = getRandomImages(galleryImages, 4);
  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    adaptiveHeight: true,
  };
  return (
    <Box className="page_content_wrap" sx={{ bgcolor: '#1e2225', minHeight: '100vh', fontFamily: FONT_FAMILY, color: '#fff', px: 0 }}>
      <Container maxWidth={false} sx={{ px: 0, py: 0, mx: 0, width: '100%' }}>
        <LanguageSwitcher language={language} setLanguage={handleLanguageChange} style="dark" floating={true} />
        <Hero
          title={t('home.hero.title')}
          subtitle={t('home.hero.subtitle')}
          image={process.env.PUBLIC_URL + '/images/20230922_154044-1-scaled.jpg'}
          ctaText={t('home.hero.ctaText')}
          ctaHref="/contact"
        />

        {/* Info Section - Modern Overlap Layout */}
        <InfoSection
          title={t('home.infoSection.title')}
          subtitle={t('home.infoSection.subtitle')}
          ctaText={t('home.infoSection.ctaText')}
        />

        {/* Gallery Section - Modern Edge-to-Edge Layout */}
        <GallerySection
          images={galleryImages}
          title={t('home.gallerySection.title')}
          description={t('home.gallerySection.description')}
        />

        {/* Group Party Section - Modern Hero Layout */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            minHeight: { xs: 350, md: 700 },
            mb: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            overflow: 'hidden',
            bgcolor: '#222',
            borderRadius: 0,
            boxShadow: 3,
            animation: 'fadeInUp 1.2s',
            pl: 0,
            pr: 0,
            mx: 0,
          }}
        >
          <img
            src={process.env.PUBLIC_URL + '/images/bg-13-transformed.jpeg'}
            alt="Group Party Hero"
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'top',
              zIndex: 1,
              filter: 'brightness(0.5)',
              borderRadius: 0,
              transition: 'filter 0.5s',
            }}
          />
          <Box
            sx={{
              position: 'relative',
              zIndex: 2,
              width: { xs: '100%', md: '55%' },
              pr: { xs: 2, md: 12 },
              pl: { xs: 2, md: 0 },
              ml: 'auto',
              textAlign: { xs: 'center', md: 'right' },
              color: '#fff',
              py: { xs: 6, md: 10 },
              animation: 'fadeInRight 1.4s',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                fontSize: { xs: 36, md: 64 },
                mb: 2,
                fontFamily: FONT_FAMILY,
                lineHeight: 1.1,
                letterSpacing: 1,
                textAlign: { xs: 'center', md: 'right' },
                animation: 'fadeInRight 1.6s',
              }}
            >
              {t('home.groupParty.title')}<br />{t('home.groupParty.titleLine2')}
            </Typography>
            <Box sx={{ width: 80, height: 6, bgcolor: '#ed5034', borderRadius: 2, mb: 3, ml: { xs: 'auto', md: 'unset' }, mr: { xs: 'auto', md: 0 }, animation: 'fadeInRight 1.8s' }} />
            <Typography
              variant="body1"
              sx={{
                color: '#bbb',
                fontSize: 18,
                maxWidth: 600,
                textAlign: { xs: 'center', md: 'right' },
                mx: { xs: 'auto', md: 0 },
                animation: 'fadeInRight 2s',
              }}
            >
              {t('home.groupParty.description')}
            </Typography>
          </Box>
        </Box>

        {/* Animations */}
        <style>{`
          @keyframes fadeInDown {
            0% { opacity: 0; transform: translateY(-40px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          @keyframes fadeInLeft {
            0% { opacity: 0; transform: translateX(-40px); }
            100% { opacity: 1; transform: translateX(0); }
          }
          @keyframes fadeInRight {
            0% { opacity: 0; transform: translateX(40px); }
            100% { opacity: 1; transform: translateX(0); }
          }
          @keyframes fadeInUp {
            0% { opacity: 0; transform: translateY(40px); }
            100% { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </Container>
    </Box>
  );
}